import { useEffect, useRef } from 'react'

/** The overlays open right now, in the order they opened: only the last answers Escape. */
const openOverlays: symbol[] = []

/**
 * Modal-overlay dismissal behaviour: Escape closes, and body scroll is locked
 * while open. Shared by the app's modals and the image lightbox so the two
 * behaviours can't drift. Overlays stack — a picker opened from inside a modal
 * form, a lightbox over an attachments modal — so Escape closes only the one
 * opened last, and the page stays locked until the last one closes. Inert
 * while `isOpen` is false.
 */
export const useOverlayDismiss = (isOpen: boolean, onClose: () => void): void => {
	// Read when Escape lands, so a new onClose each render doesn't re-register
	// the overlay and move it to the top of the stack.
	const onCloseRef = useRef(onClose)
	useEffect(() => {
		onCloseRef.current = onClose
	}, [onClose])

	useEffect(() => {
		if (!isOpen) return

		const id = Symbol('overlay')
		openOverlays.push(id)

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && openOverlays[openOverlays.length - 1] === id) onCloseRef.current()
		}

		document.addEventListener('keydown', handleEscape)
		document.body.style.overflow = 'hidden'

		return () => {
			document.removeEventListener('keydown', handleEscape)
			openOverlays.splice(openOverlays.indexOf(id), 1)
			if (openOverlays.length === 0) document.body.style.overflow = 'unset'
		}
	}, [isOpen])
}
